import React from 'react';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiMail, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Logo } from '../components/common/Logo';

interface Props {
  country: string;
  backPath: string;
}

export const CountryThankYouPage: React.FC<Props> = ({ country, backPath }) => {
  const navigate = useNavigate();

  useDocumentTitle(
    `Thank You - White Label Agency Partnership | Mirani Marketing`,
    'Thank you for your interest in our white label digital marketing services. We will get back to you soon.'
  );

  const svgBg = "bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')]";

  return (
    <div className="min-h-screen bg-white">
      <section className={`relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900 pt-0`}>
        <div className={`absolute inset-0 ${svgBg} opacity-10`}></div>

        <div className="absolute top-0 left-0 right-0 z-20 border-b border-white/20 bg-black/20 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <Logo size="medium" className="bg-white rounded-md px-2 py-1" />
            <div className="flex items-center justify-end text-right text-white/95 text-xs sm:text-sm">
              <a href="mailto:contact@miranimarketing.com" className="flex items-center gap-2 hover:text-white transition-colors">
                <FiMail className="text-blue-200" />
                <span>contact@miranimarketing.com</span>
              </a>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10 max-w-4xl py-12 pt-36 sm:pt-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, delay: 0.2, type: 'spring' }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-400/20 backdrop-blur-sm mb-8">
              <FiCheckCircle className="w-12 h-12 text-green-300" />
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Thank You for Reaching Out!
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl md:text-2xl text-white/90 mb-12 max-w-2xl mx-auto">
              We've received your partnership inquiry and are excited to learn more about your agency.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-12 border border-white/20 mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">What Happens Next?</h2>
              <div className="grid md:grid-cols-3 gap-8 text-left">
                {[
                  { step: '1', title: 'Initial Review', desc: 'Our team will review your inquiry and agency details within the next few hours.' },
                  { step: '2', title: 'Personalized Response', desc: "You'll receive a detailed email within 24 hours with next steps and partnership options." },
                  { step: '3', title: 'Discovery Call', desc: "We'll schedule a consultation to discuss how we can support your agency's growth." }
                ].map((item) => (
                  <div key={item.step} className="flex flex-col items-start">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-xl mb-4">{item.step}</div>
                    <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-white/80">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 mb-8">
              <p className="text-white mb-2"><strong>Need immediate assistance?</strong></p>
              <p className="text-white/90">
                Email us directly at{' '}
                <a href="mailto:contact@miranimarketing.com" className="font-semibold text-blue-200 hover:text-white underline">
                  contact@miranimarketing.com
                </a>
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.7 }}>
              <button onClick={() => navigate(backPath)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-lg font-semibold hover:bg-white/30 transition-all duration-300">
                <FiArrowLeft />
                Back to {country} Landing Page
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CountryThankYouPage;
